import { useLayoutEffect, useRef, useState } from 'react';

// Säulendiagramm aus dem Prototyp (colChart): gestapelte Reihen, vier Gitterlinien, Summe über der letzten Säule,
// Hinweisfeld beim Zeigen oder Fokussieren
export interface Reihe { name: string; farbe: string; werte: number[] }

const schritt = (x: number) => { const p = 10 ** Math.floor(Math.log10(x || 1)), f = x / p; return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * p; };
const obenRund = (x: number, y: number, w: number, h: number, r: number) => { r = Math.min(r, h, w / 2); return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`; };

export function Saeulen({ id, label, gruppen, reihen, format }: { id: string; label: string; gruppen: string[]; reihen: Reihe[]; format: (v: number, achse?: boolean) => string }) {
  const huelle = useRef<HTMLDivElement>(null);
  const [breite, setBreite] = useState(600);
  const [aktiv, setAktiv] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = huelle.current;
    if (!el) return;
    const messen = () => setBreite(Math.max(el.clientWidth, 240));
    messen();
    const ro = new ResizeObserver(messen);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const n = gruppen.length, W = breite, H = 200, padL = 48, padR = 8, padT = 20, padB = 24, plotH = H - padT - padB;
  const summen = gruppen.map((_, i) => reihen.reduce((a, s) => a + s.werte[i], 0));
  const st = schritt(Math.max(...summen, 0) / 4), max = st * 4;
  const band = (W - padL - padR) / n, bw = Math.min(40, band * .56), y0 = H - padB;
  return (
    <div className="viz" id={id} ref={huelle}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label}>
        {[0, 1, 2, 3, 4].map(t => {
          const y = y0 - plotH * t / 4;
          return <g key={t}><line className="grid" x1={padL} x2={W - padR} y1={y} y2={y} /><text x={padL - 8} y={y + 4} textAnchor="end">{format(st * t, true)}</text></g>;
        })}
        {gruppen.map((b, i) => {
          const x = padL + i * band + (band - bw) / 2;
          let y = y0;
          const gezeigt = reihen.filter(s => s.werte[i] > 0);
          const teile = gezeigt.map((s, k) => {
            const h = s.werte[i] / max * plotH, letzte = k === gezeigt.length - 1, hh = letzte ? h : Math.max(h - 2, 0);
            const el = letzte ? <path key={s.name} d={obenRund(x, y - h, bw, h, 4)} fill={`var(${s.farbe})`} /> : <rect key={s.name} x={x} y={y - hh} width={bw} height={hh} fill={`var(${s.farbe})`} />;
            y -= h;
            return el;
          });
          return (
            <g key={b} className={aktiv !== null && aktiv !== i ? 'col dim' : 'col'} data-i={i}>
              {teile}
              {i === n - 1 && <text x={x + bw / 2} y={y - 6} textAnchor="middle" style={{ fill: 'var(--text)', fontWeight: 600 }}>{format(summen[i])}</text>}
              <text x={x + bw / 2} y={H - 6} textAnchor="middle">{b}</text>
              <rect className="hit" x={padL + i * band} y={padT} width={band} height={plotH + padB} tabIndex={0} aria-label={`${b}: ${format(summen[i])}`}
                onMouseEnter={() => setAktiv(i)} onFocus={() => setAktiv(i)} onMouseLeave={() => setAktiv(null)} onBlur={() => setAktiv(null)} />
            </g>
          );
        })}
      </svg>
      <div className={aktiv !== null ? 'viz-tip show' : 'viz-tip'} aria-hidden="true"
        style={{ transform: `translate(${aktiv === null ? 0 : Math.min(Math.max(padL + aktiv * band + band / 2 - 70, 0), W - 160)}px, 0)` }}>
        {aktiv !== null && <>
          <strong>{gruppen[aktiv]}</strong>
          {reihen.map(s => <span className="row" key={s.name}><i className="sw" style={{ background: `var(${s.farbe})` }} />{s.name}<b>{format(s.werte[aktiv])}</b></span>)}
          {reihen.length > 1 && <span className="row">Zusammen<b>{format(summen[aktiv])}</b></span>}
        </>}
      </div>
    </div>
  );
}
